import type { CalibrationBucket, SubcategorySummary } from "../api";
import { fmtCents, fmtNum, fmtPct, fmtPp, fmtRoi, fmtUsd, signClass } from "../lib/format";
import { subMeta } from "../lib/meta";

export function SubcategoryChips({
	items,
	active,
	onPick,
}: {
	items: SubcategorySummary[];
	active?: string;
	onPick: (sub?: string) => void;
}) {
	return (
		<div className="flex flex-wrap gap-2">
			{items.map((s) => {
				const m = subMeta(s.subcategory);
				const on = active === s.subcategory;
				return (
					<button
						type="button"
						key={s.subcategory}
						title={m.blurb}
						onClick={() => onPick(on ? undefined : s.subcategory)}
						className={`btn btn-xs h-auto py-1.5 normal-case justify-start gap-2 ${on ? "btn-primary" : "btn-outline"}`}
					>
						<span className="font-semibold">{m.label}</span>
						<span className="font-mono opacity-80">{s.wallets}</span>
						<span className={`font-mono ${on ? "" : signClass(s.ev_per_usd)}`} title="Pooled ROI, dollar-weighted">
							{fmtRoi(s.ev_per_usd)}
						</span>
					</button>
				);
			})}
		</div>
	);
}

/** Reliability table: if the price is p, does it win p of the time? Bars: price (grey) vs actual win rate. */
export function CalibrationCard({ buckets }: { buckets: CalibrationBucket[] }) {
	return (
		<div className="card bg-base-200 border border-base-content/10">
			<div className="card-body p-4">
				<h3 className="text-sm font-bold font-mono">Does the price predict the outcome?</h3>
				<p className="text-xs opacity-70">
					All resolved insider-detector positions, grouped by entry price. If markets are well calibrated the
					two bars match. A gap is edge.
				</p>
				<div className="overflow-x-auto">
					<table className="table table-xs">
						<thead>
							<tr className="text-[10px] uppercase opacity-70">
								<th>Entry</th>
								<th className="text-right">n</th>
								<th className="w-40">Price vs actual</th>
								<th className="text-right">Win %</th>
								<th className="text-right">Edge</th>
								<th className="text-right">Brier</th>
							</tr>
						</thead>
						<tbody>
							{buckets.map((b) => (
								<tr key={b.bucket}>
									<td className="font-mono">{fmtCents(b.price_from)}–{fmtCents(b.price_to)}</td>
									<td className="text-right font-mono">{b.n}</td>
									<td>
										<div className="relative h-3 w-full bg-base-300 rounded-sm" title={`price ${fmtPct(b.avg_entry)} vs won ${fmtPct(b.win_rate)}`}>
											<div className="absolute inset-y-0 left-0 bg-base-content/30 rounded-sm" style={{ width: `${(b.avg_entry ?? 0) * 100}%` }} />
											<div className="absolute top-1/2 h-1.5 -translate-y-1/2 left-0 bg-primary rounded-sm" style={{ width: `${(b.win_rate ?? 0) * 100}%` }} />
										</div>
									</td>
									<td className="text-right font-mono">{fmtPct(b.win_rate)}</td>
									<td className={`text-right font-mono ${signClass(b.edge)}`}>{fmtPp(b.edge)}</td>
									<td className="text-right font-mono">{fmtNum(b.brier, 3)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}

export function Explainer() {
	return (
		<details className="collapse collapse-arrow bg-base-200 border border-base-content/10">
			<summary className="collapse-title text-sm font-bold font-mono">How to read these numbers</summary>
			<div className="collapse-content text-xs space-y-2 leading-relaxed">
				<p>
					<b>Entry price is the market&apos;s probability.</b> Buying at 30¢ means the market gave that outcome a 30% chance.
					A winning share pays $1, so it breaks even if it wins 30% of the time.
				</p>
				<p>
					<b>ROI</b> = profit ÷ dollars staked, assuming each position is held to resolution. Staking $100 at 20¢ returns
					+$400 on a win and −$100 on a loss. <b>Edge</b> = win rate minus entry price, per position: the average amount a
					wallet beats its own price.
				</p>
				<p>
					<b>Brier</b> = mean (entry − outcome)². Lower is better and 0.25 is a coin flip. It rewards being right about
					probabilities, but a wallet that only buys 95¢ favorites scores a tiny Brier with almost no edge, so read it next to Edge.
				</p>
				<p>
					<b>CLV</b> (closing line value) = current price − entry, so it works before a market resolves.
				</p>
				<p>
					<b>Weighting matters.</b> Win %, Entry, Edge and Brier count every position once. ROI and CLV weight by dollars. They
					can disagree: many small winners plus one big losing longshot gives a high win rate and a very negative ROI.
				</p>
				<p>
					<b>Don&apos;t trust small samples.</b> Use <i>Edge (95% low)</i> to rank: it subtracts uncertainty and needs 5+ resolved
					positions. With hundreds of wallets, some look brilliant by luck alone.
				</p>
				<p className="opacity-70">
					Types are heuristic rules on stored data, checked in order: market maker (blocklist), arb/hedger (both sides of 3+ markets),
					scalper (15+ trades per position), sniper (10+ fills ≥ 95¢), otherwise insider by dollar-weighted entry band. Market makers
					and hedgers don&apos;t hold to resolution, so their ROI here is a counterfactual, not their real P&amp;L.
				</p>
			</div>
		</details>
	);
}

export const Totals = ({ items }: { items: SubcategorySummary[] }) => {
	const wallets = items.reduce((a, s) => a + s.wallets, 0);
	const vol = items.reduce((a, s) => a + (s.volume_usd ?? 0), 0);
	return (
		<span className="font-mono text-xs opacity-70">
			{wallets} wallets · {fmtUsd(vol)} staked
		</span>
	);
};
